import { type RouteRecord, ViteReactSSG } from "vite-react-ssg";
import { routes } from "./App";
import { ClientContext, TransportHandler } from "@quatico/magellan-shared";
import { initProjectConfiguration } from "@quatico/magellan-client";
import { AppLayout } from "./components/AppLayout";
import { initMfeApp } from "./utils/mfe-app-shell";
import "./index.css";
import { getCpqBaseUrl } from "./utils/vite-env";

export const magellanFetchFn: TransportHandler = async (func, context: ClientContext) => {
    const { name, payload, namespace, endpoint } = func;
    try {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: { ...(context?.client?.headers ?? {}), "Content-Type": "application/json" },
            body: JSON.stringify({ name, payload, namespace }),
        });
        if (response.status >= 300) {
            throw new Error(`Failed to invoke remote function: "${name}". Reason: "${response?.statusText || ""}".`);
        }
        return await response.text();
    } catch (err) {
        throw new Error(`Cannot invoke remote function: "${name}". Reason: "${err as string}".`);
    }
};

const CPQ_AUTH_NAMESPACE = "cds-cpq-auth";

const getCurrentLanguage = (): string => {
    if (typeof window !== "undefined") {
        const stored = localStorage.getItem("quaweb-language");
        if (stored) {
            return stored;
        }
    }
    return "de";
};

const createFormData = ({
    name,
    payload,
    namespace,
}: {
    name: string;
    payload: string;
    namespace: string;
}): FormData => {
    const formData = new FormData();
    formData.set("name", name);
    formData.set("data", payload);
    formData.set("namespace", namespace);
    formData.set("language", getCurrentLanguage());
    if (namespace === CPQ_AUTH_NAMESPACE) {
        if (typeof window !== "undefined") {
            const hash = window.location.hash;
            const token = new URLSearchParams(hash.split("?")[1]).get("token");
            if (!token) {
                throw new Error("Token not found");
            }
            formData.set("token", token);
        } else {
            throw new Error("Token not supported in server environment");
        }
    }
    return formData;
};

export const cdsFetchFn: TransportHandler = async (func, context: ClientContext) => {
    const { name, payload, namespace, endpoint } = func;
    if (!name) {
        // eslint-disable-next-line quotes
        throw new Error('Cannot invoke this remote function without "name" property.');
    }
    try {
        const isBrowser = typeof window !== "undefined";
        const response = await fetch(endpoint, {
            method: "POST",
            headers: { ...(context?.client?.headers ?? {}) },
            credentials: isBrowser && window.location.hostname === "localhost" ? "include" : "same-origin",
            body: createFormData({ name, payload, namespace }),
        });
        if (response.status >= 300) {
            return Promise.reject({ status: response.status, message: response?.statusText || "" });
        }
        return await response.text();
    } catch (err) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Cannot invoke remote function: "${name}". Reason: "${err}".`);
    }
};

const cpqBaseUrl = getCpqBaseUrl();

initProjectConfiguration({
    namespaces: {
        ["quaweb-noauth"]: {
            endpoint: "/api/magellan",
            transport: "magellan-fetch",
        },
        ...(cpqBaseUrl && {
            ["cds-cpq-no-auth"]: {
                endpoint: `${cpqBaseUrl}/api/no-auth`,
                transport: "cds-fetch-transport",
            },
            ["cds-cpq-auth"]: {
                endpoint: `${cpqBaseUrl}/api/auth`,
                transport: "cds-fetch-transport",
            },
        }),
    },
    transports: {
        "magellan-fetch": magellanFetchFn,
        "cds-fetch-transport": cdsFetchFn,
    },
});

// Initialize MFE App Shell (only in browser, not during SSG build)
if (typeof window !== "undefined") {
    initMfeApp().catch(err => {
        console.error("[MFE] Bootstrap failed:", err);
    });
}

// Use a layout route to wrap all child routes with providers.
// The layout route is pathless — React Router matches it for all URLs and renders
// AppLayout (with HeadProvider, LanguageProvider, Suspense) around the matched child.
const appRoutes: RouteRecord[] = [
    {
        Component: AppLayout,
        children: routes,
    },
];

export const createRoot = ViteReactSSG(
    {
        routes: appRoutes,
        basename: "/",
    },
    ctx => {
        // During SSG: capture rendered head tags per route for static HTML injection.
        // HeadProvider stores its head on globalThis.__LAST_SSR_HEAD__ during the
        // synchronous renderToString. We read it here before any await to avoid races.
        if (!ctx.isClient) {
            const routePath = ctx.routePath;
            ctx.onSSRAppRendered(async () => {
                const head = (globalThis as Record<string, unknown>).__LAST_SSR_HEAD__;
                if (!head) {
                    return;
                }

                const { renderSSRHead } = await import("@unhead/react/server");
                const tags = await renderSSRHead(head as Parameters<typeof renderSSRHead>[0]);
                const map = ((globalThis as Record<string, unknown>).__SSG_HEAD_TAGS__ ||= {}) as Record<
                    string,
                    unknown
                >;
                map[routePath!] = tags;
            });
        }
    }
);
